import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const UserManagementGrid = ({ users, onEditUser, onToggleStatus, onBulkAction, currentUserRole = 'admin' }) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [locationFilter, setLocationFilter] = useState('all');
  const [areaFilter, setAreaFilter] = useState('all');

  // Jordanian cities and Amman areas
  const jordanianCities = [
    { value: 'all', label: 'جميع المدن' },
    { value: 'amman', label: 'عمان' },
    { value: 'zarqa', label: 'الزرقاء' },
    { value: 'irbid', label: 'إربد' },
    { value: 'aqaba', label: 'العقبة' },
    { value: 'madaba', label: 'مادبا' },
    { value: 'karak', label: 'الكرك' },
    { value: 'mafraq', label: 'المفرق' },
    { value: 'tafilah', label: 'الطفيلة' },
    { value: 'maan', label: 'معان' },
    { value: 'ajloun', label: 'عجلون' },
    { value: 'jerash', label: 'جرش' },
    { value: 'salt', label: 'السلط' }
  ];

  const ammanAreas = [
    { value: 'all', label: 'جميع المناطق' },
    { value: 'abdoun', label: 'عبدون' },
    { value: 'deir_ghbar', label: 'دير غبار' },
    { value: 'downtown', label: 'وسط البلد' },
    { value: 'jabal_amman', label: 'جبل عمان' },
    { value: 'sweifieh', label: 'الصويفية' },
    { value: 'shmeisani', label: 'الشميساني' },
    { value: 'khalda', label: 'خلدا' },
    { value: 'marj_hamam', label: 'مرج الحمام' },
    { value: 'tabarbour', label: 'طبربور' },
    { value: 'marka', label: 'ماركا' }
  ];

  // Role-based permission checks
  const hasPermission = (action) => {
    const permissions = {
      admin: ['edit', 'suspend', 'activate', 'resetPassword', 'bulkActions'],
      property_manager: ['edit'],
      contract_specialist: [],
      client_manager: []
    };
    return permissions?.[currentUserRole]?.includes(action) || false;
  };

  const roleColors = {
    admin: 'bg-[var(--color-error)] text-white',
    property_manager: 'bg-[var(--color-primary)] text-white',
    contract_specialist: 'bg-[var(--color-accent)] text-[var(--color-accent-foreground)]',
    client_manager: 'bg-[var(--color-secondary)] text-white',
  };

  const statusColors = {
    active: 'bg-[var(--color-success)] text-white',
    inactive: 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)]',
    suspended: 'bg-[var(--color-warning)] text-white',
  };

  // Filter users by location
  const filteredUsers = users?.filter(user => {
    const cityMatch = locationFilter === 'all' || user?.city === locationFilter;
    const areaMatch = areaFilter === 'all' || user?.area === areaFilter;
    return cityMatch && areaMatch;
  });

  const sortedUsers = [...filteredUsers]?.sort((a, b) => {
    const aVal = a?.[sortField];
    const bVal = b?.[sortField];
    const modifier = sortOrder === 'asc' ? 1 : -1;
    return aVal > bVal ? modifier : -modifier;
  });

  const handleSelectAll = (e) => {
    if (e?.target?.checked) {
      setSelectedUsers(filteredUsers?.map(u => u?.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev?.includes(userId) 
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  return (
    <div className="bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] shadow-warm">
      {/* Location Filters */}
      <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-muted)]">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="flex items-center gap-2">
            <Icon name="MapPin" size={20} color="var(--color-foreground)" />
            <span className="text-sm font-medium text-[var(--color-foreground)]">تصفية حسب الموقع:</span>
          </div>
          <div className="flex flex-wrap items-center gap-3 flex-1">
            <Select
              value={locationFilter}
              onChange={(e) => {
                setLocationFilter(e?.target?.value);
                if (e?.target?.value !== 'amman') {
                  setAreaFilter('all');
                }
              }}
              options={jordanianCities}
              className="min-w-[150px]"
            />
            {locationFilter === 'amman' && (
              <Select
                value={areaFilter}
                onChange={(e) => setAreaFilter(e?.target?.value)}
                options={ammanAreas}
                className="min-w-[150px]"
              />
            )}
            <span className="text-xs text-[var(--color-muted-foreground)]">
              عرض {filteredUsers?.length} من {users?.length} موظف
            </span>
          </div>
        </div>
      </div>

      {selectedUsers?.length > 0 && hasPermission('bulkActions') && (
        <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-muted)] flex items-center justify-between gap-4">
          <span className="text-sm font-medium text-[var(--color-foreground)]">
            تم تحديد {selectedUsers?.length} مستخدم
          </span>
          <div className="flex items-center gap-2">
            {hasPermission('suspend') && (
              <Button
                variant="outline"
                size="sm"
                iconName="Lock"
                onClick={() => onBulkAction('suspend', selectedUsers)}
              >
                تعليق الحسابات
              </Button>
            )}
            {hasPermission('activate') && (
              <Button
                variant="outline"
                size="sm"
                iconName="Unlock"
                onClick={() => onBulkAction('activate', selectedUsers)}
              >
                تفعيل الحسابات
              </Button>
            )}
            {hasPermission('resetPassword') && (
              <Button
                variant="outline"
                size="sm"
                iconName="Key"
                onClick={() => onBulkAction('resetPassword', selectedUsers)}
              >
                إعادة تعيين كلمة المرور
              </Button>
            )}
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[var(--color-muted)] border-b border-[var(--color-border)]">
            <tr>
              <th className="p-3 text-right">
                {hasPermission('bulkActions') && (
                  <input
                    type="checkbox"
                    checked={selectedUsers?.length === filteredUsers?.length}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-[var(--color-border)]"
                  />
                )}
              </th>
              <th className="p-3 text-right text-sm font-semibold text-[var(--color-foreground)]">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 hover:text-[var(--color-primary)] transition-colors"
                >
                  المستخدم
                  <Icon name={sortField === 'name' ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={16} />
                </button>
              </th>
              <th className="p-3 text-right text-sm font-semibold text-[var(--color-foreground)]">
                <button
                  onClick={() => handleSort('role')}
                  className="flex items-center gap-2 hover:text-[var(--color-primary)] transition-colors"
                >
                  الدور
                  <Icon name={sortField === 'role' ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={16} />
                </button>
              </th>
              <th className="p-3 text-right text-sm font-semibold text-[var(--color-foreground)]">البريد الإلكتروني</th>
              <th className="p-3 text-right text-sm font-semibold text-[var(--color-foreground)]">الموقع</th>
              <th className="p-3 text-right text-sm font-semibold text-[var(--color-foreground)]">
                <button
                  onClick={() => handleSort('lastLogin')}
                  className="flex items-center gap-2 hover:text-[var(--color-primary)] transition-colors"
                >
                  آخر تسجيل دخول
                  <Icon name={sortField === 'lastLogin' ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={16} />
                </button>
              </th>
              <th className="p-3 text-right text-sm font-semibold text-[var(--color-foreground)]">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-2 hover:text-[var(--color-primary)] transition-colors"
                >
                  الحالة
                  <Icon name={sortField === 'status' ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={16} />
                </button>
              </th>
              <th className="p-3 text-right text-sm font-semibold text-[var(--color-foreground)]">الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers?.map((user) => (
              <tr key={user?.id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-muted)] transition-colors">
                <td className="p-3">
                  {hasPermission('bulkActions') && (
                    <input
                      type="checkbox"
                      checked={selectedUsers?.includes(user?.id)}
                      onChange={() => handleSelectUser(user?.id)}
                      className="w-4 h-4 rounded border-[var(--color-border)]"
                    />
                  )}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <Image
                      src={user?.avatar}
                      alt={user?.avatarAlt}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-[var(--color-foreground)]">{user?.name}</p>
                      <p className="text-xs text-[var(--color-muted-foreground)]">{user?.department}</p>
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${roleColors?.[user?.role]}`}>
                    {user?.roleLabel}
                  </span>
                </td>
                <td className="p-3">
                  <p className="text-sm text-[var(--color-foreground)]">{user?.email}</p>
                </td>
                <td className="p-3">
                  <div className="text-sm text-[var(--color-foreground)]">
                    <p className="font-medium">{user?.cityLabel || 'غير محدد'}</p>
                    {user?.areaLabel && (
                      <p className="text-xs text-[var(--color-muted-foreground)]">{user?.areaLabel}</p>
                    )}
                  </div>
                </td>
                <td className="p-3">
                  <p className="text-sm text-[var(--color-muted-foreground)]">{user?.lastLogin}</p>
                </td>
                <td className="p-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusColors?.[user?.status]}`}>
                    <Icon name={user?.status === 'active' ? 'CheckCircle' : user?.status === 'suspended' ? 'AlertCircle' : 'XCircle'} size={12} />
                    {user?.statusLabel}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    {hasPermission('edit') && (
                      <Button
                        variant="ghost"
                        size="icon"
                        iconName="Edit"
                        onClick={() => onEditUser(user)}
                        title="تعديل المستخدم"
                      />
                    )}
                    {(hasPermission('suspend') || hasPermission('activate')) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        iconName={user?.status === 'active' ? 'Lock' : 'Unlock'}
                        onClick={() => onToggleStatus(user?.id, user?.status)}
                        title={user?.status === 'active' ? 'تعليق الحساب' : 'تفعيل الحساب'}
                      />
                    )}
                    {!hasPermission('edit') && !hasPermission('suspend') && !hasPermission('activate') && (
                      <span className="text-xs text-[var(--color-muted-foreground)] px-2">لا توجد صلاحيات</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManagementGrid;